// src/Components/TopBar/SearchBar.js

import React, { useState, useEffect, useRef, useCallback } from 'react';
import Box from '@mui/material/Box';
import OutlinedInput from '@mui/material/OutlinedInput';
import InputAdornment from '@mui/material/InputAdornment';
import FormControl from '@mui/material/FormControl';
import IconButton from '@mui/material/IconButton';
import SearchIcon from '@mui/icons-material/Search';
import CancelIcon from '@mui/icons-material/Cancel';
import CircularProgress from '@mui/material/CircularProgress';
import Typography from '@mui/material/Typography';
import Switch from '@mui/material/Switch';
import Tooltip from '@mui/material/Tooltip';

// Autocomplete Dropdown Imports
import Popper from '@mui/material/Popper';
import Paper from '@mui/material/Paper';
import MenuList from '@mui/material/MenuList';
import MenuItem from '@mui/material/MenuItem';
import ClickAwayListener from '@mui/material/ClickAwayListener';
import Grow from '@mui/material/Grow';

// Firebase
import { collection, getDocs, doc, getDoc } from 'firebase/firestore';
import { db } from '../../Firebase/firebase';

// --- ANIMATION VARIABLES ---
const SPLASH_DURATION = '0.8s';
const SPLASH_COLOR = 'rgba(0, 85, 255, 0.15)';
const SPLASH_WIDTH = '50%';

// --- SYSTEM KEYS TO IGNORE IN SEARCH ---
const EXCLUDED_KEYS = new Set([
  'id',
  'icon',
  'iconID',
  'initialStyleIcon',
  'markerGroupID',
  'markerID',
  'polygonID',
  'polylineID',
  'url',
  'img',
  'hoverImage',
  'lastModified',
  'timestamp',
  'date',
  'publicFromDate',
  'file',
  'fileObject',
  'color',
  'weight',
  'isDrawing',
  'isPrivate',
  'visibility',
  'showcase',
  'categoryId',
  'regionId',
  'fileName',
  'type',
  'uploadedAt',
]);

// Utility to escape regex characters safely for whole word matching
const escapeRegExp = (string) => {
  return string.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
};

/**
 * SearchBar Component
 * Handles Map (Google Places API New) and Info (Firestore Markers) searching.
 */
export default function SearchBar({ setSearchMarker, resetMap, isLocked }) {
  // --- STATE ---
  const [searchText, setSearchText] = useState('');
  const [searchMode, setSearchMode] = useState('MAP');
  const [dynamicPlaceholder, setDynamicPlaceholder] = useState('Search Google Maps');
  const [isSplashing, setIsSplashing] = useState(false);

  // Unified Dropdown State
  const [filteredInfoResults, setFilteredInfoResults] = useState([]);
  const [mapSearchResults, setMapSearchResults] = useState([]);
  const [infoSearchAnchorEl, setInfoSearchAnchorEl] = useState(null);
  const [isSearchingInfo, setIsSearchingInfo] = useState(false);

  // Refs
  const searchInputRef = useRef(null);

  // Places API (New) Library references
  const [placesLib, setPlacesLib] = useState(null);
  const [sessionToken, setSessionToken] = useState(null);

  const infoSearchOpen = Boolean(infoSearchAnchorEl);

  // AMENDED: Memory-leak safe polling logic
  useEffect(() => {
    let checkInterval = null;
    let isMounted = true; // Prevents state updates and intervals on unmounted component

    const attemptLoadPlaces = async () => {
      if (!isMounted) return false;

      if (window.google?.maps?.importLibrary) {
        try {
          const lib = await window.google.maps.importLibrary('places');
          if (isMounted) {
            setPlacesLib(lib);
            setSessionToken(new lib.AutocompleteSessionToken());
            if (checkInterval) clearInterval(checkInterval);
          }
          return true;
        } catch (err) {
          console.error('Failed to load Places library:', err);
          return false;
        }
      }
      return false;
    };

    attemptLoadPlaces().then((success) => {
      if (!success && isMounted) {
        checkInterval = setInterval(attemptLoadPlaces, 500);
      }
    });

    return () => {
      isMounted = false; // Kill switch for async tasks
      if (checkInterval) clearInterval(checkInterval);
    };
  }, []);

  // New structure for Address Extraction using addressComponents
  const extractAddressDetailsNew = (place) => {
    const components = place.addressComponents || [];
    let streetNumber = '';
    let route = '';

    const details = {
      address: '',
      city: '',
      state: '',
      country: '',
      zip: '',
    };

    components.forEach((c) => {
      const types = c.types;
      if (types.includes('street_number')) streetNumber = c.longText;
      else if (types.includes('route')) route = c.longText;
      else if (types.includes('locality')) details.city = c.longText;
      else if (types.includes('administrative_area_level_1')) details.state = c.shortText;
      else if (types.includes('country')) details.country = c.longText;
      else if (types.includes('postal_code')) details.zip = c.longText;
    });

    details.address = `${streetNumber} ${route}`.trim();
    if (!details.address) {
      details.address = place.displayName || place.formattedAddress || '';
    }

    return details;
  };

  const handlePlaceResult = useCallback(
    (location, details) => {
      if (!location) return;

      const lat = typeof location.lat === 'function' ? location.lat() : location.lat;
      const lng = typeof location.lng === 'function' ? location.lng() : location.lng;

      if (setSearchMarker) {
        setSearchMarker({
          lat,
          lng,
          ...details,
        });
      } else if (resetMap) {
        resetMap({ center: { lat, lng }, zoom: 17 });
      }
    },
    [resetMap, setSearchMarker],
  );

  // Programmatic Autocomplete Fetching for MAP mode
  useEffect(() => {
    if (searchMode !== 'MAP' || !searchText.trim() || !placesLib) {
      setMapSearchResults([]);
      return;
    }

    const fetchPredictions = async () => {
      try {
        const request = {
          input: searchText,
          sessionToken: sessionToken,
        };
        const { suggestions } =
          await placesLib.AutocompleteSuggestion.fetchAutocompleteSuggestions(request);

        const places = suggestions
          .filter((s) => s.placePrediction)
          .map((s) => s.placePrediction);

        setMapSearchResults(places);
        setInfoSearchAnchorEl(searchInputRef.current);
      } catch (e) {
        console.error('Autocomplete fetch error:', e);
      }
    };

    const timeoutId = setTimeout(fetchPredictions, 300); // Debounce to prevent spamming
    return () => clearTimeout(timeoutId);
  }, [searchText, searchMode, placesLib, sessionToken]);

  // Handle when user clicks a Map Suggestion
  const handleMapResultSelect = async (prediction) => {
    setInfoSearchAnchorEl(null);
    setIsSearchingInfo(true);

    try {
      const place = prediction.toPlace();
      await place.fetchFields({
        fields: ['location', 'displayName', 'formattedAddress', 'addressComponents'],
      });

      // Rotate token upon successful selection to separate billing sessions
      setSessionToken(new placesLib.AutocompleteSessionToken());

      const details = extractAddressDetailsNew(place);
      handlePlaceResult(place.location, details);
      setSearchText(place.formattedAddress || place.displayName || '');
    } catch (err) {
      console.error('Error fetching place details:', err);
    } finally {
      setIsSearchingInfo(false);
    }
  };

  const executeSearch = useCallback(async () => {
    if (!searchText.trim() || isLocked) return;

    if (searchMode === 'MAP') {
      if (searchInputRef.current) searchInputRef.current.blur();
      setInfoSearchAnchorEl(null);
      setIsSearchingInfo(true);

      try {
        if (placesLib) {
          const { places } = await placesLib.Place.searchByText({
            textQuery: searchText,
            // AMENDED: Strictly required by Google Maps Places API v2
            fields: ['location', 'displayName', 'formattedAddress', 'addressComponents'],
          });
          if (places && places.length > 0) {
            const place = places[0];
            await place.fetchFields({
              fields: [
                'location',
                'displayName',
                'formattedAddress',
                'addressComponents',
              ],
            });
            const details = extractAddressDetailsNew(place);
            handlePlaceResult(place.location, details);
            setSearchText(place.formattedAddress || place.displayName || '');
          }
        }
      } catch (err) {
        console.error('Text search error:', err);
      } finally {
        setIsSearchingInfo(false);
      }
    }

    if (searchMode === 'INFO') {
      if (searchInputRef.current) searchInputRef.current.blur();
      setIsSearchingInfo(true);

      try {
        const groupsSnap = await getDocs(collection(db, 'markerGroups'));
        const groupsData = groupsSnap.docs.map((doc) => ({ id: doc.id, ...doc.data() }));

        // Normalize user input to prevent multiple spaces from breaking exact match
        const rawPhrase = searchText.toUpperCase().trim().replace(/\s+/g, ' ');
        const searchTerms = rawPhrase.split(' ');
        const termCount = searchTerms.length;

        const extractAllText = (obj) => {
          if (!obj) return '';
          if (typeof obj === 'string') {
            // Strip HTML tags and replace with a space to maintain word boundaries
            return obj.replace(/<[^>]*>?/gm, ' ').toUpperCase();
          }
          if (typeof obj === 'number' || typeof obj === 'boolean') {
            return String(obj).toUpperCase();
          }
          if (typeof obj === 'object') {
            return Object.entries(obj)
              .filter(([key]) => !EXCLUDED_KEYS.has(key))
              .map(([_, val]) => extractAllText(val))
              .join(' ');
          }
          return '';
        };

        const scoredResults = [];

        groupsData.forEach((group) => {
          // Flatten string and normalize multiple spaces/newlines to a single space
          const searchableText = extractAllText(group).replace(/\s+/g, ' ');
          let score = 0;
          let matchReasons = [];

          // TIER 1: Exact Phrase Match
          const tier1Regex = new RegExp(`\\b${escapeRegExp(rawPhrase)}\\b`);
          if (tier1Regex.test(searchableText)) {
            score += 1000;
            matchReasons.push(`TIER 1: (Score: 1000) Exact Phrase -> "${rawPhrase}"`);
          }

          // TIER 2: 3-Word Combos (N-Grams)
          if (termCount > 3) {
            for (let i = 0; i <= termCount - 3; i++) {
              const nGram = searchTerms.slice(i, i + 3).join(' ');
              const regex = new RegExp(`\\b${escapeRegExp(nGram)}\\b`);
              if (regex.test(searchableText)) {
                score += 300;
                matchReasons.push(`TIER 2: (Score: 300) 3-Word Combo -> "${nGram}"`);
              }
            }
          }

          // TIER 3: 2-Word Combos (N-Grams)
          if (termCount > 2) {
            for (let i = 0; i <= termCount - 2; i++) {
              const nGram = searchTerms.slice(i, i + 2).join(' ');
              const regex = new RegExp(`\\b${escapeRegExp(nGram)}\\b`);
              if (regex.test(searchableText)) {
                score += 200;
                matchReasons.push(`TIER 3: (Score: 200) 2-Word Combo -> "${nGram}"`);
              }
            }
          }

          // TIER 4 & 5: Single Words (Whole vs Partial)
          searchTerms.forEach((term) => {
            const regexWhole = new RegExp(`\\b${escapeRegExp(term)}\\b`);

            if (regexWhole.test(searchableText)) {
              // TIER 4: Whole Word
              score += 50;
              matchReasons.push(`TIER 4: (Score: 50) Whole Word -> "${term}"`);
            } else if (searchableText.includes(term)) {
              // TIER 5: Partial Word (Only runs if Whole Word fails)
              score += 10;
              matchReasons.push(`TIER 5: (Score: 10) Partial Match -> "${term}"`);
            }
          });

          // Only keep results that scored at least something
          if (score > 0) {
            scoredResults.push({
              group: group,
              score: score,
              logs: matchReasons,
            });
          }
        });

        // Sort descending by highest score
        scoredResults.sort((a, b) => b.score - a.score);

        const top20Results = scoredResults.slice(0, 20);

        // Map the array back to purely group objects for React state to prevent freezing the flyto
        setFilteredInfoResults(top20Results.map((r) => r.group));
        setInfoSearchAnchorEl(searchInputRef.current);
      } catch (err) {
        console.error('Error fetching live markerGroups for search:', err);
      } finally {
        setIsSearchingInfo(false);
      }
    }
  }, [searchText, searchMode, handlePlaceResult, isLocked, placesLib]);

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') executeSearch();
  };

  const handleInfoResultSelect = async (group) => {
    setInfoSearchAnchorEl(null);
    setIsSearchingInfo(true);

    try {
      let lat, lng;
      let geomData = null;

      if (group.markerID) {
        const snap = await getDoc(doc(db, 'markers', group.markerID));
        if (snap.exists()) geomData = snap.data();
      }

      if (!geomData && group.polygonID) {
        const snap = await getDoc(doc(db, 'polygons', group.polygonID));
        if (snap.exists()) geomData = snap.data();
      }

      if (!geomData && group.polylineID) {
        const snap = await getDoc(doc(db, 'polylines', group.polylineID));
        if (snap.exists()) geomData = snap.data();
      }

      if (geomData) {
        if (geomData.position && geomData.position.lat !== undefined) {
          lat =
            typeof geomData.position.lat === 'function'
              ? geomData.position.lat()
              : geomData.position.lat;
          lng =
            typeof geomData.position.lng === 'function'
              ? geomData.position.lng()
              : geomData.position.lng;
        } else if (
          geomData.paths &&
          geomData.paths.length > 0 &&
          geomData.paths[0].length > 0
        ) {
          const firstPoint = geomData.paths[0][0];
          lat = typeof firstPoint.lat === 'function' ? firstPoint.lat() : firstPoint.lat;
          lng = typeof firstPoint.lng === 'function' ? firstPoint.lng() : firstPoint.lng;
        } else if (geomData.path && geomData.path.length > 0) {
          const firstPoint = geomData.path[0];
          lat = typeof firstPoint.lat === 'function' ? firstPoint.lat() : firstPoint.lat;
          lng = typeof firstPoint.lng === 'function' ? firstPoint.lng() : firstPoint.lng;
        }
      }

      if (lat !== undefined && lng !== undefined) {
        handlePlaceResult(
          { lat, lng },
          { address: group.address || group.title || group.locationId },
        );
      } else {
        console.warn('Coordinates could not be found for this markerGroup.');
      }
    } catch (err) {
      console.error('Error fetching geometry for search result:', err);
    } finally {
      setIsSearchingInfo(false);
    }
  };

  const handleClearSearch = () => {
    if (isLocked) return;
    setSearchText('');
    setFilteredInfoResults([]);
    setMapSearchResults([]);
    setInfoSearchAnchorEl(null);
    if (setSearchMarker) setSearchMarker(null);
    if (searchInputRef.current) searchInputRef.current.focus();
  };

  const handleSwitchChange = (event) => {
    if (isLocked) return;
    const isInfo = event.target.checked;
    const newMode = isInfo ? 'INFO' : 'MAP';
    setSearchMode(newMode);
    setSearchText('');
    setDynamicPlaceholder(
      newMode === 'MAP' ? 'Search Google Maps' : 'Search loKtr Markers',
    );
    setFilteredInfoResults([]);
    setMapSearchResults([]);
    setInfoSearchAnchorEl(null);
    if (setSearchMarker) setSearchMarker(null);
    setIsSplashing(true);
    setTimeout(() => setIsSplashing(false), 800);
  };

  const handlePopperClose = (event) => {
    if (searchInputRef.current && searchInputRef.current.contains(event.target)) {
      return;
    }
    setInfoSearchAnchorEl(null);
  };

  return (
    <Box
      sx={{
        width: '100%',
        maxWidth: '500px', // Prevents it from getting too long and squishing the menu
        display: 'flex',
        alignItems: 'center',
        gap: 1.5,
        ml: '30px',
        mr: 2,
      }}>
      <Tooltip
        title={
          isLocked
            ? 'Editing in progress'
            : searchMode === 'MAP'
              ? 'Switch to loKtr Search'
              : 'Switch to Map Search'
        }>
        <span>
          <Switch
            checked={searchMode === 'INFO'}
            onChange={handleSwitchChange}
            disabled={isLocked}
            color='primary'
            size='small'
            sx={{
              '& .MuiSwitch-switchBase.Mui-checked': { color: '#0055ff' },
              '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                backgroundColor: '#0055ff',
              },
            }}
          />
        </span>
      </Tooltip>

      <FormControl
        variant='outlined'
        fullWidth
        sx={{ height: '36px', justifyContent: 'center' }}>
        <OutlinedInput
          id='search-location'
          placeholder={dynamicPlaceholder}
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
          onKeyDown={handleKeyDown}
          readOnly={isLocked}
          inputRef={searchInputRef}
          sx={{
            borderRadius: '50px',
            height: '36px',
            backgroundColor: isLocked ? 'rgba(0,0,0,0.02)' : 'white',
            fontSize: '0.875rem',
            transition: 'all 0.3s ease',

            '& .MuiOutlinedInput-input': {
              '&:-webkit-autofill': {
                transitionDelay: '9999s',
                transitionProperty: 'background-color, color',
              },
            },

            '& .MuiOutlinedInput-notchedOutline': {
              borderColor: 'rgba(0,0,0,0.2)',
              borderWidth: '1px',
            },
            '&:hover .MuiOutlinedInput-notchedOutline': {
              borderColor: isLocked ? 'rgba(0,0,0,0.2)' : 'rgba(0,0,0,0.4)',
            },
            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
              borderColor: '#0055ff',
              borderWidth: '2px',
            },
            ...(isSplashing && {
              backgroundImage: `linear-gradient(90deg, transparent, ${SPLASH_COLOR}, transparent)`,
              backgroundSize: `${SPLASH_WIDTH} 100%`,
              animation: `leftRightSplash ${SPLASH_DURATION} ease-in-out`,
              '@keyframes leftRightSplash': {
                '0%': { backgroundPosition: '-50% 0' },
                '100%': { backgroundPosition: '150% 0' },
              },
            }),
          }}
          startAdornment={
            <InputAdornment position='start' sx={{ pl: 0.5 }}>
              {isSearchingInfo ? (
                <CircularProgress size={18} thickness={5} />
              ) : (
                <IconButton
                  size='small'
                  onClick={executeSearch}
                  disabled={isLocked}
                  sx={{
                    padding: '2px',
                    transition: 'transform 0.2s',
                    '&:hover': {
                      transform: isLocked ? 'none' : 'scale(1.15)',
                      backgroundColor: 'transparent',
                      color: isLocked ? 'inherit' : '#0055ff',
                    },
                  }}>
                  <SearchIcon fontSize='small' />
                </IconButton>
              )}
            </InputAdornment>
          }
          endAdornment={
            searchText && (
              <InputAdornment position='end'>
                <IconButton
                  onClick={handleClearSearch}
                  size='small'
                  disabled={isLocked}
                  sx={{
                    p: '2px',
                    '&:hover': { color: isLocked ? 'inherit' : '#ff1744' },
                  }}>
                  <CancelIcon sx={{ fontSize: 18 }} />
                </IconButton>
              </InputAdornment>
            )
          }
        />
        <Popper
          open={infoSearchOpen && !isLocked}
          anchorEl={infoSearchAnchorEl}
          placement='bottom-start'
          transition
          style={{ zIndex: 100020, width: searchInputRef.current?.offsetWidth }}>
          {({ TransitionProps }) => (
            <Grow {...TransitionProps}>
              <Paper
                elevation={6}
                sx={{
                  maxHeight: 400,
                  overflow: 'auto',
                  mt: 1,
                  borderRadius: '8px',
                  border: '1px solid rgba(0,0,0,0.1)',
                }}>
                <ClickAwayListener onClickAway={handlePopperClose}>
                  <MenuList sx={{ py: 1 }}>
                    {searchMode === 'INFO' ? (
                      filteredInfoResults.length > 0 ? (
                        filteredInfoResults.map((m) => {
                          const locDetails = [m.address, m.city, m.state]
                            .filter(Boolean)
                            .join(', ');

                          return (
                            <MenuItem
                              key={m.id}
                              onClick={() => handleInfoResultSelect(m)}
                              sx={{
                                px: 2,
                                py: 1,
                                '&:hover': { backgroundColor: 'rgba(0,85,255,0.05)' },
                              }}>
                              <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                                <Typography variant='subtitle2' color='primary' noWrap>
                                  {m.title ||
                                    m.locationId ||
                                    m.companyName ||
                                    'Unnamed Location'}
                                </Typography>
                                <Typography
                                  variant='caption'
                                  color='text.secondary'
                                  noWrap>
                                  {locDetails || m.companyName || 'No location details'}
                                </Typography>
                              </Box>
                            </MenuItem>
                          );
                        })
                      ) : (
                        <MenuItem disabled>
                          <Typography variant='body2' sx={{ fontStyle: 'italic' }}>
                            No matching loKtr markers found
                          </Typography>
                        </MenuItem>
                      )
                    ) : mapSearchResults.length > 0 ? (
                      mapSearchResults.map((prediction, idx) => (
                        <MenuItem
                          key={idx}
                          onClick={() => handleMapResultSelect(prediction)}
                          sx={{
                            px: 2,
                            py: 1,
                            '&:hover': { backgroundColor: 'rgba(0,85,255,0.05)' },
                          }}>
                          <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                            <Typography variant='subtitle2' color='primary' noWrap>
                              {prediction.text.text}
                            </Typography>
                          </Box>
                        </MenuItem>
                      ))
                    ) : (
                      <MenuItem disabled>
                        <Typography variant='body2' sx={{ fontStyle: 'italic' }}>
                          No places found
                        </Typography>
                      </MenuItem>
                    )}
                  </MenuList>
                </ClickAwayListener>
              </Paper>
            </Grow>
          )}
        </Popper>
      </FormControl>
    </Box>
  );
}
